import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionLayoutWaehlen: Lesson = {
  id: "layout-waehlen",
  title: "Das passende Layout wählen",
  summary: "Layouts sind fertige Bauanleitungen – und nachträglich austauschbar.",
  durationMinutes: 10,
  learningGoals: [
    "Den Layoutkatalog kennen und passend auswählen",
    "Das Layout einer bestehenden Folie nachträglich wechseln",
    "Erkennen, warum ein Layoutwechsel besser ist als Verschieben von Hand",
  ],
  steps: [
    {
      id: "layoutkatalog",
      title: "Was ein Layout festlegt",
      explanation: [
        "Ein Layout ist eine Bauanleitung für eine Folie: Welche Platzhalter gibt es, " +
          "wo sitzen sie, wie groß ist die Schrift darin, welche Aufzählungszeichen " +
          "werden verwendet. Ein typischer Master bringt neun bis elf Layouts mit.",
        "Die wichtigsten vier decken fast alles ab. „Titelfolie“ eröffnet die " +
          "Präsentation. „Titel und Inhalt“ ist das Arbeitspferd – Titel oben, darunter " +
          "ein Bereich für Text, Tabelle, Diagramm oder Bild. „Abschnittsüberschrift“ " +
          "trennt Kapitel voneinander. „Zwei Inhalte“ stellt zwei Dinge nebeneinander, " +
          "etwa Text und Bild.",
        "Daneben gibt es „Nur Titel“ für Folien, die frei gestaltet werden, und " +
          "„Leer“ für ganzseitige Bilder. Beide sind legitim – aber sie sind die " +
          "Ausnahme, nicht der Normalfall. Wer eine Präsentation überwiegend aus " +
          "„Leer“-Folien baut, hat den Baukasten ausgeschaltet.",
      ],
      keyPoints: [
        "Start → Layout zeigt alle Layouts des aktuellen Folienmasters.",
        "Das Layout bestimmt Platzhalter, Position und Schriftgrößen.",
        "„Leer“ und „Nur Titel“ sind Ausnahmen, nicht der Standard.",
      ],
    },
    {
      id: "inhaltsfolie",
      title: "Titel und Inhalt zuweisen",
      explanation: [
        "Für die zweite Folie ist „Titel und Inhalt“ die richtige Wahl. Der " +
          "Inhaltsplatzhalter darin ist bemerkenswert flexibel: Solange er leer ist, " +
          "zeigt er kleine Symbole für Tabelle, Diagramm, SmartArt, Bild und Video. Ein " +
          "Klick darauf setzt das Objekt genau in den vorgesehenen Bereich.",
        "Genau darin liegt der praktische Vorteil gegenüber dem Weg über die " +
          "Registerkarte „Einfügen“: Ein über den Platzhalter eingefügtes Diagramm sitzt " +
          "automatisch richtig und passt seine Größe an, wenn sich das Layout ändert. " +
          "Ein über „Einfügen“ platziertes Diagramm landet in der Folienmitte und bleibt " +
          "dort liegen, egal was das Design später sagt.",
      ],
      keyPoints: [
        "Der leere Inhaltsplatzhalter bietet Tabelle, Diagramm, SmartArt und Bild an.",
        "Über den Platzhalter eingefügte Objekte übernehmen Position und Größe.",
        "Start → Layout wechselt das Layout einer bestehenden Folie.",
      ],
      task: {
        instruction:
          "Weise deiner zweiten Folie das Layout „Titel und Inhalt“ zu und schreibe einen " +
          "Titel hinein.",
        hint: "Folie 2 markieren, dann Start → Layout → „Titel und Inhalt“.",
        check: async () => {
          const snapshot = await readPresentation();
          const zweite = snapshot.slides[1];

          if (!zweite) {
            return fail(
              "Es gibt noch keine zweite Folie.",
              "Lege mit Strg + M eine weitere Folie an.",
            );
          }
          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Das Layout einer Folie",
              "1.3",
              "Kontrolliere unter „Start“ → „Layout“, welches Layout markiert ist.",
            );
          }

          const name = zweite.layoutName ?? "";
          const passt =
            zweite.layoutType === "Object" ||
            zweite.layoutType === "TextAndObject" ||
            /titel und inhalt|title and content/i.test(name);

          return fromCriteria(
            [
              criterion(
                `Folie 2 nutzt ein Inhaltslayout (zugewiesen: „${name || "unbekannt"}“)`,
                passt,
              ),
              criterion(
                "Die Folie hat einen Titelplatzhalter mit Text",
                zweite.shapes.some(
                  (form) =>
                    form.type === "Placeholder" &&
                    form.paragraphs.length > 0 &&
                    (form.placeholder?.type === "Title" ||
                      /^(title|titel)\b/i.test(form.name ?? "")),
                ),
              ),
            ],
            {
              success: `Folie 2 steht auf „${name}“ und hat einen Titel.`,
              failure: "Da fehlt noch etwas an Folie 2.",
              hint:
                "Die Layoutnamen sind sprachabhängig. Entscheidend ist, dass das Layout " +
                "einen Titel- und einen Inhaltsplatzhalter mitbringt – im deutschen " +
                "PowerPoint heißt es „Titel und Inhalt“.",
            },
          );
        },
      },
    },
    {
      id: "layout-wechseln",
      title: "Layout wechseln statt verschieben",
      explanation: [
        "Der typische Moment: Eine Folie mit Aufzählung soll zusätzlich ein Bild rechts " +
          "bekommen. Der naheliegende Weg ist, den Textplatzhalter mit der Maus schmaler " +
          "zu ziehen und das Bild daneben zu setzen. Das sieht auf dem eigenen Bildschirm " +
          "gut aus – und rutscht auf dem Beamer, in der PDF oder nach einem Designwechsel " +
          "wieder auseinander.",
        "Der richtige Weg ist ein Layoutwechsel: „Start“ → „Layout“ → „Zwei Inhalte“. " +
          "PowerPoint verteilt die vorhandenen Inhalte auf die neuen Platzhalter und die " +
          "Folie ist wieder Teil des Baukastens.",
        "Ein Layoutwechsel ist jederzeit möglich und geht nicht auf Kosten des Inhalts. " +
          "Was in Platzhaltern steht, wandert mit. Was in freien Textfeldern liegt, bleibt " +
          "genau da liegen, wo es war – ein weiterer Grund, Platzhalter zu benutzen.",
      ],
      keyPoints: [
        "Start → Layout wechselt jederzeit, ohne Inhalte zu verlieren.",
        "Inhalte in Platzhaltern wandern mit, Inhalte in freien Textfeldern nicht.",
        "„Zurücksetzen“ (Start → Zurücksetzen) stellt die Platzhalter des Layouts wieder her.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass deine Präsentation mindestens zwei verschiedene Layouts " +
          "verwendet – zum Beispiel „Titelfolie“ für Folie 1 und „Titel und Inhalt“ für " +
          "die übrigen.",
        hint: "Start → Layout, für jede Folie einzeln.",
        check: async () => {
          const snapshot = await readPresentation();

          if (snapshot.slides.length < 2) {
            return fail(
              "Für diese Aufgabe braucht es mindestens zwei Folien.",
              "Lege mit Strg + M eine weitere Folie an.",
            );
          }
          if (!snapshot.features.shapes) {
            return nichtPruefbar(
              "Das Layout einer Folie",
              "1.3",
              "Klicke jede Folie an und sieh unter „Start“ → „Layout“ nach, welches Layout markiert ist.",
            );
          }

          const layouts = new Set(
            snapshot.slides.map((folie) => folie.layoutName ?? folie.layoutId ?? "unbekannt"),
          );

          if (layouts.size >= 2) {
            return pass(
              `Es sind ${layouts.size} verschiedene Layouts im Einsatz: ${[...layouts]
                .map((name) => `„${name}“`)
                .join(", ")}.`,
            );
          }
          return fail(
            `Alle Folien nutzen dasselbe Layout („${[...layouts][0]}“).`,
            "Die Titelfolie sollte das Layout „Titelfolie“ tragen, die Inhaltsfolien „Titel und Inhalt“.",
          );
        },
      },
    },
  ],
};
